import { isNaN, toNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { DNSlideTabItemType } from '@/designComponents/organisms/DNSlideTab/DNSlideTabItem';
import useSetAuthOnScreenFocus from '@/hooks/auth/useSetAuthOnScreenFocus';
import useGetUserAddressByLocation from '@/hooks/hospital/search/useGetUserAddressByLocation';
import useGetKeywordIntegratedSearchDoctor from '@/hooks/search/useGetKeywordIntegratedSearchDoctor';
import useGetKeywordIntegratedSearchHospital from '@/hooks/search/useGetKeywordIntegratedSearchHospital';
import useGetPopularKeywordsForDiseaseSymptomSearch from '@/hooks/search/useGetPopularKeywordsForDiseaseSymptomSearch';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal, { AddModalProps } from '@/hooks/useModal';
import useToast from '@/hooks/useToast';
import { recentHomeSearchKeywordListState } from '@/recoil/atoms/search';
import { SearchHospitalService } from '@/services/hospital/SearchHospitalService';
import { HomeSearchService } from '@/services/search/HomeSearchService';
import { Client } from '@/types/app';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategoryType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { APP_SCREEN_NAME, ROUTES } from '@/types/common';
import { RegionByDepthVo, SearchHospitalVo, UserCoordinate } from '@/types/hospital';
import { Magazine } from '@/types/magazine';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import {
  DoctorSearchResultVo,
  HomeSearchKeywordList,
  IntegratedSearchTabType,
  MagazineSearchResultVo,
  NonF2fDoctorVo,
  QuestionSearchResultVo,
  SearchResult,
} from '@/types/search';
import { coreLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { deleteQueryString, updateSearchParams } from '@/utils/url';

import useGetKeywordIntegratedSearchMagazine from './useGetKeywordIntegratedSearchMagazine';
import useGetKeywordIntegratedSearchPost from './useGetKeywordIntegratedSearchPost';
import useGetKeywordIntegratedSearchQna from './useGetKeywordIntegratedSearchQna';

interface Modal {
  isOpen: boolean;
  info: AddModalProps;
}

const INITIAL_MODAL_STATE = {
  isOpen: false,
  info: {
    type: undefined,
    props: {
      title: '',
      buttons: [],
    },
  },
};

const INTEGRATED_SEARCH_RESULT_TABS = [
  {
    key: 0,
    label: '통합',
    value: IntegratedSearchTabType.All,
  },
  {
    key: 1,
    label: '비대면 진료',
    value: IntegratedSearchTabType.Doctor,
  },
  {
    key: 2,
    label: '방문 예약',
    value: IntegratedSearchTabType.Hospital,
  },
  {
    key: 3,
    label: '실시간 의료상담',
    value: IntegratedSearchTabType.Qna,
  },
  {
    key: 4,
    label: '건강 매거진',
    value: IntegratedSearchTabType.Magazine,
  },
];

export interface HomeSearchViewModel {
  handleInputChange: (value: string) => void;
  handleSnackBarClose: () => void;
  handleSubmit: () => void;
  handleResetSearchKeyword: (e?: React.MouseEvent) => void;
  handleSearchDirectKeyword: (keyword: string) => void;
  handleDeleteItemRecentKeyword: (keyword: string) => void;
  handleDeleteAllRecentKeyword: () => void;
  handleChangeKeyword: () => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  keyword: string;
  isSnackBarOpen: boolean;
  recentKeywordViewData: HomeSearchKeywordList;
  isVisibleRecentKeyword: boolean;
  setIsVisibleRecentKeyword: React.Dispatch<React.SetStateAction<boolean>>;
  handleClickRequestButton: (buttonPosition: string) => void;
  hasNoData: boolean;
  modal: Modal;
  handleClickQuestion: (questionId: number | string) => void;
  handleClickMagazine: (magazine: Magazine) => Promise<void>;
  handleClickDoctor: (doctor: DoctorSearchResultVo) => Promise<void>;
  handleClickMoreData: (type: IntegratedSearchTabType) => () => void;
  handleSymptomKeywordClick: (keyword: string) => void;
  popularSymptomList: Array<{
    symptomName: string;
    symptomPublicId: string;
  }>;
  isSearchResultShow: boolean;
  INTEGRATED_SEARCH_RESULT_TABS: Array<DNSlideTabItemType<IntegratedSearchTabType>>;
  selectedTabValue: IntegratedSearchTabType;
  onClickTabItem: (tab: DNSlideTabItemType<IntegratedSearchTabType>) => void;
  onChangeTabByMoreData: (type: IntegratedSearchTabType) => void;
  keywordSearchQna?: SearchResult<QuestionSearchResultVo>;
  keywordSearchMagazine?: SearchResult<MagazineSearchResultVo>;
  keywordSearchDoctor?: SearchResult<DoctorSearchResultVo>;
  keywordSearchHospital?: SearchResult<SearchHospitalVo>;
  goToAppHospital: (hospitalPid: string) => void;
  getHeaderAddress: () => string;
  requestGeolocationPermission: () => void;
  coordinate: UserCoordinate;
  geolocationPermissionLoading: boolean;
  doctors: Array<NonF2fDoctorVo>;
  hasMoreDoctors: boolean;
  category: { category: MedicalCategoryType; id: string } | null;
}

type Props = {
  client: Client;
  entryType: string;
  keyword: string;
};

const DOCTOR_LIST_LIMIT = 4;

const useHomeSearchViewModel = (props: Props): HomeSearchViewModel => {
  useSetAuthOnScreenFocus();
  const { router, moveToPage, pageName } = useCustomRouter();
  const { keyword: searchParam, entryType } = props;
  const { latitude: queryLatitude, longitude: queryLongitude } = router.query as {
    latitude: string;
    longitude: string;
  };

  const initialCoord = useMemo(() => {
    if (!queryLatitude || !queryLongitude) {
      return SearchHospitalService.initializeUserCoordinate();
    }

    const latitude = toNumber(queryLatitude);
    const longitude = toNumber(queryLongitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      return SearchHospitalService.initializeUserCoordinate();
    }

    return { latitude, longitude };
  }, [queryLatitude, queryLongitude]);
  const [regionByDepth, setRegionByDepth] = useState<RegionByDepthVo>();
  const [coordinate, setCoordinate] = useState<UserCoordinate>(initialCoord);

  const { data: sigunguDongli = SearchHospitalService.initializeUserSigunguDongli(), refetch: addressRefetch } =
    useGetUserAddressByLocation(coordinate.latitude, coordinate.longitude);

  const { keywordSearchQna } = useGetKeywordIntegratedSearchQna(searchParam);
  const { keywordSearchPost } = useGetKeywordIntegratedSearchPost(searchParam);
  const { keywordSearchMagazine } = useGetKeywordIntegratedSearchMagazine(searchParam);
  const { keywordSearchDoctor } = useGetKeywordIntegratedSearchDoctor(searchParam);
  const { keywordSearchHospital } = useGetKeywordIntegratedSearchHospital(
    searchParam,
    coordinate.latitude,
    coordinate.longitude
  );
  const { items, hasNextPage, category } = useInfiniteSearchNonF2fDoctors(
    {
      request: {
        keyword: searchParam,
        sortType: DoctorSortType.Random,
        page: 1,
        limit: DOCTOR_LIST_LIMIT,
        doctorType: DoctorType.Doctor,
        telemedicineEligibleTargetType: TelemedicineEligibleTargetType.NightAndHolidayOnly,
      },
    },
    { enabled: !!searchParam }
  );

  const trimmedDoctors = [...items].splice(0, DOCTOR_LIST_LIMIT - 1);

  const { closeModal: closeCurrentModal } = useModal();
  const { showToast } = useToast();

  const shouldSnackBarOpen = router.query.shouldSnackBarOpen as string;
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [isSearchResultShow, setIsSearchResultShow] = useState<boolean>(!!searchParam ?? false);
  const [inputValue, setInputValue] = useState<string>(searchParam);
  const [keyword, setKeyword] = useState<string>(searchParam);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [recentKeywordList, setRecentKeywordList] = useRecoilState(recentHomeSearchKeywordListState);
  const [modal, setModal] = useState<Modal>({ ...INITIAL_MODAL_STATE });
  const [geolocationPermissionLoading, setGeolocationPermissionLoading] = useState(false);
  const [recentKeywordViewData, setRecentKeywordViewData] = useState<HomeSearchKeywordList>([]);
  const [isVisibleRecentKeyword, setIsVisibleRecentKeyword] = useState<boolean>(searchParam ? false : true);
  const [selectedTabValue, setSelectedTabValue] = useState<IntegratedSearchTabType>(
    (router.query.tab as IntegratedSearchTabType) ?? IntegratedSearchTabType.All
  );

  const getHeaderAddress = () => {
    let address = `${sigunguDongli.sigungu} ${sigunguDongli.dongLi}`;

    if (regionByDepth?.siDoName) {
      if (!regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName}`;
      } else if (regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName} ${regionByDepth.siGunGuName}`;
      } else if (regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siGunGuName} ${regionByDepth.legalEupMyeonDongName}`;
      } else if (!regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName} ${regionByDepth.legalEupMyeonDongName}`;
      }
    }
    return address;
  };

  const initRegionFilter = () => {
    setRegionByDepth(undefined);
  };

  const requestGeolocationPermission = useCallback(() => {
    if (!navigator.geolocation) {
      showToast({
        message: '다른 브라우저로 해당 기능을 이용해주세요',
      });
      setCoordinate({
        latitude: initialCoord.latitude,
        longitude: initialCoord.longitude,
      });
      return;
    }
    setGeolocationPermissionLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        initRegionFilter();
        setGeolocationPermissionLoading(false);
        updateSearchParams([
          {
            key: 'latitude',
            value: coords.latitude,
          },
          {
            key: 'longitude',
            value: coords.longitude,
          },
        ]);
      },
      () => {
        setGeolocationPermissionLoading(false);
        showToast({
          message: '위치 사용을 허용을 해야 내 위치 설정이 가능해요',
        });
      },
      { enableHighAccuracy: true }
    );
  }, [initialCoord, showToast]);

  const setSearchKeyword = async (newKeyword: string) => {
    setIsSearchResultShow(true);
    setKeyword(newKeyword.trim());
    await updateSearchParams([{ key: 'keyword', value: newKeyword }]);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const closeModal = () => {
    closeCurrentModal();
    setModal(INITIAL_MODAL_STATE);
  };

  const handleSubmit = () => {
    const newKeywordList = HomeSearchService.getNewKeywordList([...recentKeywordList], keyword);

    setRecentKeywordList(newKeywordList);
    setIsVisibleRecentKeyword(false);
    searchInputRef.current?.blur();
    router.push({ query: { keyword, tab: selectedTabValue } });
  };

  // 검색 버튼 클릭
  const handleChangeKeyword = async () => {
    if (!inputValue.trim()) {
      setModal({
        isOpen: true,
        info: {
          type: MODAL_TYPE.ALERT,
          props: {
            title: '검색어를 입력해주세요.',
            buttons: [
              {
                type: MODAL_BUTTON_TYPE.BOLD,
                label: '확인',
                onClick: () => closeModal(),
              },
            ],
          },
        },
      });

      return;
    }

    if (inputValue.length < 2) {
      setModal({
        isOpen: true,
        info: {
          type: MODAL_TYPE.ALERT,
          props: {
            title: '2글자 이상 입력해 주세요.',
            message: '더 정확도 높은 결과를 볼 수 있어요.',
            buttons: [
              {
                type: MODAL_BUTTON_TYPE.BOLD,
                label: '확인',
                onClick: () => closeModal(),
              },
            ],
          },
        },
      });

      return;
    }

    coreLogger({
      eventName: 'click_search',
      pageName,
      payload: {
        section: 'search_bar',
        keyword: inputValue,
        entry_point: entryType,
      },
    });

    await setSearchKeyword(inputValue);
  };

  const handleResetSearchKeyword = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setInputValue('');
    searchInputRef.current?.blur();
    setIsVisibleRecentKeyword(true);
    setIsSearchResultShow(false);
    deleteQueryString('keyword');
  };

  // 최근 검색어 / 자주 찾는 검색어로 검색
  const handleSearchDirectKeyword = async (keyword: string) => {
    coreLogger({
      eventName: 'click_search',
      pageName,
      payload: {
        section: 'recent',
        keyword,
        entry_point: entryType,
      },
    });
    await setSearchKeyword(keyword);
    setInputValue(keyword);
  };

  // 최근 검색어 선택 삭제
  const handleDeleteItemRecentKeyword = (keyword: string): void => {
    const filter = recentKeywordViewData.filter((recentKeyword) => keyword !== recentKeyword);

    setRecentKeywordList(filter);
  };

  // 최근 검색어 전체 삭제
  const handleDeleteAllRecentKeyword = (): void => {
    setRecentKeywordList([]);
  };

  const handleSnackBarClose = (): void => {
    setIsSnackBarOpen(false);
  };

  const handleClickRequestButton = (buttonPosition: string): void => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName,
      payload: {
        position: buttonPosition,
        keyword,
      },
    });
    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+'), entryType).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+'), entryType).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  const handleClickQuestion = (questionId: number | string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: questionId,
        tab: IntegratedSearchTabType.All,
        result_section: IntegratedSearchTabType.Qna,
      },
    });

    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionId}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: {
        questionId,
        result_section: IntegratedSearchTabType.Qna,
      },
    });
  };

  const handleClickDoctor = async (doctor: DoctorSearchResultVo) => {
    if (!isWebView()) {
      return;
    }

    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: doctor.doctorId,
        tab: IntegratedSearchTabType.All,
        result_section: IntegratedSearchTabType.Doctor,
      },
    });

    sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
      screenName: APP_SCREEN_NAME.DOCTOR_DETAIL,
      params: {
        doctorId: doctor.doctorId,
        type: 'subject',
        entry: 'search',
      },
    });
  };

  const handleClickMagazine = async (magazine: Magazine) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: magazine.magazinePid,
        tab: IntegratedSearchTabType.All,
        result_section: IntegratedSearchTabType.Magazine,
      },
    });
    moveToPage({
      webLink: `${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`,
      mobileScreenName: ROUTES.MAGAZINE.DETAIL.appScreenName,
      params: {
        id: magazine.magazinePid,
      },
    });
  };

  const goToAppHospital = (hospitalPid: string) => {
    if (isWebView()) {
      coreLogger({
        eventName: 'search_item_click',
        pageName,
        payload: {
          keyword,
          content_id: hospitalPid,
          tab: IntegratedSearchTabType.All,
          result_section: IntegratedSearchTabType.Hospital,
        },
      });
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: ROUTES.HOSPITAL.DETAIL.appScreenName,
        params: { hospitalPid: hospitalPid },
      });
    }
  };

  const handleClickMoreData = useCallback(
    (type: IntegratedSearchTabType) => () => {
      coreLogger({
        eventName: 'search_item_more_click',
        pageName,
        payload: {
          keyword,
          result_section: type,
        },
      });
      moveToPage({
        webLink: `${ROUTES.COMMON.SEARCH.path}?${
          entryType ? `entryType=${entryType}` : ''
        }&keyword=${keyword.replaceAll(' ', '+')}`,
        mobileScreenName: ROUTES.COMMON.SEARCH.appScreenName,
        params: {
          keyword,
        },
      });
    },
    [keyword]
  );

  const onChangeTabByMoreData = (type: IntegratedSearchTabType) => {
    coreLogger({
      eventName: 'search_item_more_click',
      pageName,
      payload: {
        keyword,
        result_section: type,
      },
    });
    if (isWebView() && type === IntegratedSearchTabType.Doctor && category) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_LIST_WITH_TARGET_CHECK, {
        type: category.category,
        id: category.id,
      });
      return;
    }
    setSelectedTabValue(type);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const { popularSymptomList } = useGetPopularKeywordsForDiseaseSymptomSearch();

  const handleSymptomKeywordClick = async (keyword: string) => {
    setIsSearchResultShow(true);
    await setSearchKeyword(keyword);
    setInputValue(keyword);
    coreLogger({
      eventName: 'click_search',
      pageName,
      payload: {
        keyword,
        section: 'popular_symptom',
        entry_point: entryType,
      },
    });

    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+'), entryType).SEARCH_RESULT.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+'), entryType).SEARCH_RESULT.appScreenName,
      params: {
        keyword,
      },
    });
  };

  // 탭 클릭 handler
  const onClickTabItem = async (tab: DNSlideTabItemType<IntegratedSearchTabType>) => {
    coreLogger({
      eventName: 'integrated_search_tab_click',
      pageName,
      payload: {
        tab: tab.value,
      },
    });

    setSelectedTabValue(tab.value);
    await updateSearchParams([{ key: 'tab', value: tab.value }]);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!shouldSnackBarOpen) {
      return;
    }

    setIsSnackBarOpen(true);
    router.replace('/search');
  }, []);

  useEffect(() => {
    if (keyword) {
      handleSubmit();
      setIsSearchResultShow(true);
    }
  }, [keyword]);

  useEffect(() => {
    if (!router.query.keyword) {
      setKeyword('');
      setIsSearchResultShow(false);
    }
  }, [router.asPath, router.query.keyword]);

  useEffect(() => {
    setRecentKeywordViewData(recentKeywordList);
  }, [recentKeywordList]);

  useEffect(() => {
    const focusInput = () => searchInputRef?.current?.focus();
    focusInput();
  }, []);

  useEffect(() => {
    if (queryLatitude && queryLongitude) {
      setCoordinate({
        latitude: toNumber(queryLatitude),
        longitude: toNumber(queryLongitude),
      });
    }
  }, [queryLatitude, queryLongitude]);

  useEffect(() => {
    addressRefetch();
  }, [coordinate]);

  return {
    isVisibleRecentKeyword,
    handleInputChange,
    handleSnackBarClose,
    handleSubmit,
    handleResetSearchKeyword,
    handleSearchDirectKeyword,
    handleDeleteItemRecentKeyword,
    handleDeleteAllRecentKeyword,
    handleChangeKeyword,
    searchInputRef,
    inputValue,
    keyword,
    isSnackBarOpen,
    recentKeywordViewData: recentKeywordViewData.filter((item) => typeof item === 'string'),
    setIsVisibleRecentKeyword,
    handleClickRequestButton,
    modal,
    handleClickQuestion,
    handleClickDoctor,
    handleClickMagazine,
    handleClickMoreData,
    handleSymptomKeywordClick,
    popularSymptomList,
    isSearchResultShow,
    INTEGRATED_SEARCH_RESULT_TABS,
    selectedTabValue,
    onClickTabItem,
    onChangeTabByMoreData,
    keywordSearchQna,
    keywordSearchMagazine,
    keywordSearchDoctor,
    keywordSearchHospital,
    goToAppHospital,
    getHeaderAddress,
    requestGeolocationPermission,
    coordinate,
    geolocationPermissionLoading,
    hasNoData:
      !keywordSearchQna?.elements.length &&
      !keywordSearchPost?.elements.length &&
      !keywordSearchMagazine?.elements.length &&
      !keywordSearchHospital?.elements.length &&
      !keywordSearchDoctor?.elements.length,
    doctors: trimmedDoctors,
    hasMoreDoctors: !!hasNextPage,
    category,
  };
};

export default useHomeSearchViewModel;
